import { useAppDispatch } from "@/store/hooks";
import { updateElement } from "@/store/slideEditorSlice";
import { getRevealedIndexes } from "@ai-teach/canvas-core";
import {
  CANVAS_DEFAULT_FONT_FAMILY,
  answerColors,
  getRequiredSpellingElementHeight,
  getSpellingStyles,
} from "@ai-teach/canvas-styles";
import type { SpellingAnswer, SpellingElement } from "@ai-teach/types";
import { useEffect } from "react";

interface SpellingRendererProps {
  element: SpellingElement;
  isLiveMode?: boolean;
  answer?: SpellingAnswer | undefined;
}

/**
 * Authoring/preview view: one box per letter of `element.word`. Revealed boxes show their letter
 * (matching what the student will see pre-filled), every other box is empty — the word and which
 * letters are revealed are edited via the property toolbar, not inline here.
 * Box size is derived from the element's own frame (`element.size`), so dragging the resize
 * handle on the canvas resizes the letter boxes — see getSpellingStyles for why.
 */
export function SpellingRenderer({ element, isLiveMode = false, answer }: SpellingRendererProps) {
  const dispatch = useAppDispatch();
  const { word } = element;
  const revealedIndexes = getRevealedIndexes(element);
  const styles = getSpellingStyles(
    element.size.width,
    element.size.height,
    word.length,
    element.boxScale
  );

  // Editor-only: grow (never shrink) the element's persisted height so every wrapped row is
  // visible without clipping — the teacher sees the same final footprint students will. Never
  // runs in live-mode preview, which only renders whatever height is already persisted.
  useEffect(() => {
    if (isLiveMode) return;
    const requiredHeight = getRequiredSpellingElementHeight(
      element.size.width,
      element.size.height,
      word.length,
      element.boxScale
    );
    if (requiredHeight > element.size.height) {
      dispatch(
        updateElement({
          id: element.id,
          size: { width: element.size.width, height: requiredHeight },
        })
      );
    }
  }, [
    isLiveMode,
    element.id,
    element.size.width,
    element.size.height,
    word.length,
    element.boxScale,
    dispatch,
  ]);

  return (
    <div
      style={{
        ...styles.outerDiv,
        borderRadius: element.border.cornerRadius,
        backgroundColor: element.backgroundColor ?? "transparent",
      }}
    >
      <div style={styles.wordRow}>
        {word.split("").map((letter, index) => {
          const isRevealed = revealedIndexes.has(index);
          const typedLetter = answer?.letters[`${index}`];
          const isTyped = !isRevealed && typedLetter !== undefined;
          const isCorrect = typedLetter === letter;

          return (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: letters are a fixed-length sequence, no stable id
              key={`letter-${index}`}
              style={{
                ...styles.letterBox,
                ...(isRevealed ? styles.letterBoxRevealed : {}),
                ...(isTyped ? (isCorrect ? styles.letterBoxCorrect : styles.letterBoxWrong) : {}),
                ...(isTyped ? { color: "#fff", position: "relative" } : {}),
                fontSize: styles.letterFontSize,
                fontFamily: CANVAS_DEFAULT_FONT_FAMILY,
              }}
            >
              {isRevealed ? letter : isTyped ? typedLetter || "—" : ""}
              {isTyped && !isCorrect && (
                // The letter they should have typed, in the corner: the boxes are sized to the
                // element's frame and have no room to grow.
                <span
                  style={{
                    position: "absolute",
                    bottom: -2,
                    right: -2,
                    width: styles.boxSize * 0.45,
                    height: styles.boxSize * 0.45,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: answerColors.correct,
                    border: "1px solid #fff",
                    fontSize: styles.letterFontSize * 0.45,
                    fontWeight: 700,
                    color: "#fff",
                  }}
                >
                  {letter}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
